import { useEffect, useState } from "react";
import { TERMINAL_STATUSES } from "../engine/jobResults";

/**
 * jobDisplay — small presentational primitives shared by the job list table
 * and the job detail view (StatusBadge, OutcomeRow) plus their formatters.
 * Lifted out of JobsTab so the research and console surfaces stay one
 * implementation (F05 FR9).
 */

/**
 * The badge tint per lifecycle status. EXPORTED so a test can assert it holds no key OUTSIDE the
 * enforced domain: the label map is covered in one direction (every status has a label) and the
 * style map had no cover in the other, which is how `approved` and `evaluating` survived here
 * after migration 090 removed both from the database.
 */
export const STATUS_STYLES = {
  completed: "bg-ok-tint text-ok",
  // E02.7 — a managed-upload job is admitted but not yet routed while its inputs
  // upload. It is a neutral WAITING state (never green/orange/red, which carry
  // approve/interactive/veto semantics), matching pending/evaluating: no decision
  // exists yet and it is invisible to the dispatch queue.
  waiting_for_inputs: "bg-surface2 text-fg2",
  // queued/dispatching/dispatched are the E08 in-flight states between routing
  // and running: no outcome yet, so a neutral treatment (never green/orange/red,
  // which carry approve/interactive/veto semantics) matching the other
  // pre-execution waiting states (pending/evaluating). `dispatched` shares this
  // treatment; it is non-terminal (see jobResults.TERMINAL_STATUSES) so the
  // poller keeps polling until the job reaches running/terminal.
  queued: "bg-surface2 text-fg2",
  dispatching: "bg-surface2 text-fg2",
  dispatched: "bg-surface2 text-fg2",
  running: "bg-info-tint text-info",
  pending: "bg-surface2 text-fg2",
  failed: "bg-danger-tint text-danger",
  vetoed: "bg-danger-tint text-danger",
  rejected: "bg-danger-tint text-danger",
  cancelled: "bg-surface2 text-fg2",
  // E23 S13 — WARN, not danger, and the distinction is the point of the status. A credit stop
  // is not a fault in the workload (danger is what `failed`/`vetoed`/`rejected` carry) and not
  // a neutral settling either: it is an unsuccessful terminal the customer can act on by
  // topping up. Painting it red would tell them to go debug a job that was running correctly,
  // which is the exact confusion US2 exists to remove.
  credit_exhausted: "bg-warn-tint text-warn",
};

// OMEGA-828 removed `DecisionBadge` and its `DECISION_STYLES` verdict->tint map from
// here. The Jobs list was its only caller, so the export had none left; keeping an
// exported, tested component that nothing renders is the kind of dead code that reads as
// live. (`DecisionTranscript.jsx` was cited here as unaffected because it never imported
// from this module; it has since been deleted as an orphan of the same kind.)

export const fmtTime = (t) => (t ? new Date(t).toLocaleString() : "n/a");

// Cost is CALCULATED in USD and DISPLAYED in AUD (billing-owner decision, OMEGA-811).
//
// Where the converted figure exists is now surface-specific, and this module must not
// assume the job payload carries one:
//
//   * The JOB payloads (GET /jobs, GET /jobs/{id}) carry ONLY `cost_actual`, the
//     provider's USD figure. AWS publishes its Sydney rates in USD and
//     `scheduler/providers/` applies no FX at all, deliberately, so converting each term
//     would compound rounding through the rate. Everything that settles against a job
//     (the usage ledger, credit capture, margin) reads this one, and NOTHING renders it.
//     `cost_actual_aud` / `fx_rate_usd_aud` briefly rode these payloads and were removed:
//     both were derived from the provider's cost, so adding them widened the
//     provider-cost surface the customer contract publishes. `cost_actual` itself remains
//     only because it predates that work; retiring it is OMEGA-899.
//   * The BILLING rollup (GET /tenants/me/usage) does carry `cost_actual_aud` +
//     `fx_rate_usd_aud`, and they stay. There the conversion fixes a real defect rather
//     than adding a field: the rollup paired its USD sum with `billing_accounts.currency`
//     and understated the screen by roughly the FX rate.
//
// The rule that outlives both: never render a USD figure with an AUD label. At fx_usd_aud
// 1.4325 that understates by ~30% while looking entirely normal, which is the bug the
// split exists to prevent. A figure you cannot denominate must read `n/a`, never the other
// currency's number relabelled.
//
// Currency is a per-CALL argument rather than a module constant, because the Billing
// rollup carries its OWN `currency` from `billing_accounts` and appends it itself;
// hardcoding a symbol made a USD account render the contradiction `A$0.5000 USD`.
const CURRENCY_SYMBOLS = { AUD: "A$", USD: "US$" };

/**
 * The customer-facing money figure is DEFERRED (owner decision, 2026-09-11). It
 * is not missing, not loading and not broken, and the placeholder below says so
 * on the screen because a customer cannot otherwise tell those apart.
 *
 * Why: the credit system is in active development, so the amount a customer is
 * actually charged is not settled yet. Rather than show Omega's provider cost
 * under a "Cost" label, the figure is withdrawn until the credit work lands.
 * `jobs.cost_actual` is Omega's cost base; the customer's price is their quoted
 * price plus margin, which is `job_outcomes.actual_revenue` (settlement.py sets
 * it from the chosen candidate's `expected_revenue` on a "fixed-quote basis").
 * Measured against a seeded database, the displayed cost understated the
 * customer's price on 84 of 84 settled jobs, by ~26% on average.
 *
 * READ THIS BEFORE DELETING ANYTHING IT TOUCHES. Migration 086, the FX oracle
 * behind it and the Billing rollup currency fix are all CORRECT and deliberately
 * RETAINED. The conversion was sound; it was simply applied to the wrong
 * quantity. The deferred work switches onto `actual_revenue` and reuses that
 * machinery, so the frontend not reading a converted figure today is NOT
 * evidence the machinery is dead.
 *
 * What is NOT retained, on the job payloads specifically: `cost_actual_aud` and
 * `fx_rate_usd_aud`. Deferring the display left them shipped-but-unread on the
 * customer job contract, and because they are derived from the provider's
 * `cost_actual` that meant this branch WIDENED the provider-cost surface while
 * the cleanup of the pre-existing `cost_actual` exposure (OMEGA-899) was still
 * deferred. They were removed from the job models, both routes and all three
 * OpenAPI documents. They remain on the Billing rollup, which is a different
 * response and a different decision. When the display returns it reattaches to
 * `actual_revenue`, so nothing here needs them back.
 *
 * Constraints on the copy, all load-bearing: no number in any currency, no date
 * and no promise (so "not yet available", never "coming soon"), and no styling
 * that reads as a spinner or an error.
 */
export const COST_DEFERRED_VALUE = "Not yet available";
export const COST_DEFERRED_NOTE =
  "Pricing is not shown at this time. This is deliberate, not a missing value.";

export const fmtMoney = (v, currency) => {
  // Treat null/undefined as "not priced yet". Without the null guard
  // Number(null) === 0 would render an unpriced cost as a real "0.0000"
  // charge — see GET /jobs cost_actual (null until the job is terminal).
  // "n/a" carries NO symbol: "A$n/a" would read as a malformed amount, and the
  // point of the null branch is that there is no amount to state.
  if (v == null) return "n/a";
  const n = Number(v);
  if (!Number.isFinite(n)) return "n/a";
  // An unrecognised code is printed verbatim rather than dropped: a verbose label is
  // better than a wrong one, and silently unlabelling it would hide the gap.
  const symbol = currency ? (CURRENCY_SYMBOLS[currency] ?? `${currency} `) : "";
  return `${symbol}${n.toFixed(4)}`;
};

// There is deliberately NO per-status wrapper around `fmtMoney` for job cost. A revision
// of this file added one that rendered 0 for a `failed` job, reasoning that an undelivered
// workload is not billable. The money path does not agree, and the money path is the
// authority: `terminal_settlement.py` states "failed/cancelled/timed-out outcomes DO
// accrue", passes the real cost to `accrue_usage`, and `capture_hold` debits the
// customer's credit for it. So the Jobs list claimed A$0.0000 for a job whose amount was
// inside the Billing period sum on the next screen -- two customer surfaces contradicting
// each other about one job.
//
// It was wrong twice over: the zero branch ran BEFORE the null guard, so a failed job with
// no cost figure at all rendered a definite 0 where every other status correctly renders
// "n/a", inverting the missing-is-not-zero rule it cited. And it zeroed `failed` while
// passing `cancelled` through, so two undelivered jobs rendered differently.
//
// If "a failed delivery is not billed" is the commercial rule, it belongs in
// `terminal_settlement.py` -- do not accrue, do not capture -- so the ledger, Billing and
// Jobs agree by construction. A formatter cannot make a billing decision the money path
// has not made.

// Customer-facing label for a raw status value (heuristic 2 — match the real
// world, never a raw enum token). Only multi-word statuses need it today.
/**
 * E24.4 FR4 (owner decision D4, 2026-09-23) — what a customer is told a job's state is.
 *
 * Before this there was ONE entry here and `statusLabel` returned the raw token for everything
 * else, so `DISPATCHING`, `VETOED` and `REJECTED` reached the screen as enum values. The keys are
 * the eleven values `jobs_status_chk` enforces (E24.2 migration 090); a key missing from this map
 * is a status the customer would see raw, which is why the coverage is asserted in the test
 * against `EXACT_STATUSES` rather than left to review.
 *
 * These name the OUTCOME. The REASON a job did not run already reaches the customer through
 * `jobs.rejection_reason`, which OMEGA-898 fills with sentences like "No available provider offers
 * the GPU type and count this workload requires." A label restating that would duplicate better
 * copy and drift away from it.
 *
 * "Not accepted" versus "Not run" is the load-bearing distinction and is legible with no internal
 * knowledge: `rejected` means no routing decision was ever committed for the job; `vetoed` means it
 * was routed and no provider could serve it. Collapsing them would hide from a customer that one of
 * the two is usually fixable on their side. The draft's "no suitable venue" is not used: `venue` is
 * internal vocabulary.
 *
 * KNOWN GAP, corrected here only as far as the comment goes (2026-10-08). This said `rejected`
 * meant "admission refused the submission ... and NO job was ever taken on". That is true of the
 * admission gate and false of the OTHER writer: `dispatch_worker._reap_orphaned_pending_creates`
 * stamps `rejected` on a job whose reservation and idempotency claim had already committed, so a
 * job WAS taken on and the fault is ours. "Not accepted" therefore tells that customer to go fix
 * something on their side when the server dropped a create it had accepted. Splitting the label is
 * a copy decision, not a comment fix, and is reported rather than taken here. Authority for the
 * writers is docs/database/jobs.md.
 */
const STATUS_LABELS = {
  pending: "Submitted",
  waiting_for_inputs: "Waiting for your files",
  queued: "Waiting for capacity",
  dispatching: "Starting",
  dispatched: "Starting",
  running: "Running",
  completed: "Completed",
  failed: "Failed",
  // NEUTRAL, and this is the load-bearing entry. `cancelled` covers three different events and
  // only ONE of them is a customer cancelling: the platform's two deadline kills write the same
  // status. This is the label used when nothing substantiates WHO stopped the job -- which is
  // every list surface, since `failure_class` is an `OutcomeSummary` field and `JobListItem` does
  // not carry it. "Cancelled" here would attribute the act to the customer on no evidence.
  cancelled: "Stopped",
  vetoed: "Not run",
  rejected: "Not accepted",
  // E23 S13 (US2) — it must read as a BILLING outcome, not a fault, because the whole purpose of
  // giving this its own terminal status is so the customer tops up instead of debugging a job
  // that was running correctly. "Stopped" matches the `cancelled` label above, so the two read
  // as the same KIND of event, and the clause after it supplies the reason the bare status
  // cannot. Deliberately not "Insufficient credit", which names an account state rather than
  // telling them what happened to their workload.
  credit_exhausted: "Stopped, out of credit",
};

/**
 * E24.4 FR4b — a `cancelled` job's label comes from HOW it was stopped, never from the status.
 *
 * The execution poller's two deadline kills both write `status='cancelled'`, so today a customer
 * whose job hit the execution time limit is told it was *cancelled*, which reads as though they
 * did it. Only `user_cancelled` is a real cancellation.
 *
 * An unrecognised or NULL class falls back to the NEUTRAL "Stopped", never to "Cancelled" -- read
 * the fallback in `statusLabel`, which resolves to `STATUS_LABELS.cancelled`. This paragraph said
 * "Cancelled ... the safe direction" until 2026-10-08, describing behaviour the FR4b amendment had
 * already replaced three lines below it. "Cancelled" is NOT the safe direction: only
 * `user_cancelled` is evidence that a customer acted, a NULL class is evidence of nothing, and
 * naming the actor there invents a cause the platform cannot substantiate. "Stopped" is true under
 * all three causes and silent about who.
 */
const CANCELLED_LABELS = {
  // The ONE class that proves a customer acted. Everything else falls back to the neutral
  // "Stopped" above rather than to this, because only `user_cancelled` is evidence of agency.
  user_cancelled: "Cancelled",
  timeout: "Stopped: time limit reached",
  // NOT "capacity was withdrawn". The writer stamps this class when capacity NEVER APPEARED --
  // its own failure_reason reads "no GPU capacity within deadline=Ns (waited Ns, container never
  // started)". "Withdrawn" asserts capacity once existed and points the customer at the wrong
  // explanation: the action here is to try another region or time, not to ask what was taken away.
  capacity_unavailable: "Stopped: no capacity was available",
};

/**
 * The customer-facing label for a lifecycle status.
 *
 * E24.4 FR4b, as amended 2026-09-24: **no surface labels a `cancelled` job in a way that claims
 * an actor the platform cannot substantiate.** Where `failure_class` is available the label names
 * the cause; where it is not, the label is neutral and claims nothing.
 *
 * The original wording named a MECHANISM -- "derived from `outcome.failure_class`, never from the
 * status alone" -- where it meant a GUARANTEE, and was therefore **unsatisfiable**: `failure_class`
 * lives on `OutcomeSummary`, so `GET /jobs` cannot return it and no list surface can read it. An
 * unsatisfiable requirement is the one that gets quietly waived, and this one duly was: the list
 * said "Cancelled" for a job the platform had killed, which is the exact defect FR4b exists to
 * prevent, sitting inside the requirement written to prevent it.
 *
 * Falling back to "Cancelled" was ALSO wrong, and the PRD called it "the safe direction" and
 * "under-claims rather than inventing a cause". It does not under-claim. Only `user_cancelled`
 * proves a customer acted; a NULL class proves nothing, and ~10 of the 149 cancelled rows on Dev
 * carry none. Neutral "Stopped" is the actual safe direction: true under all three causes, silent
 * about who.
 */
export function statusLabel(status, failureClass) {
  if (!status) return "unknown";
  if (status === "cancelled") {
    // `Object.hasOwn`, not a bare index. `job_outcomes.failure_class` is UNCONSTRAINED free text
    // (varchar(64), no CHECK), so a value of "constructor" / "toString" / "valueOf" resolves up
    // the prototype chain and the lookup returns a FUNCTION -- truthy, so `||` does not catch it,
    // and React throws on rendering it. `status` cannot do this (jobs_status_chk pins it to
    // eleven values) but `failure_class` has no such guard, and this is the one place a raw
    // database string is used as a property key.
    return Object.hasOwn(CANCELLED_LABELS, failureClass ?? "")
      ? CANCELLED_LABELS[failureClass]
      : STATUS_LABELS.cancelled;
  }
  return Object.hasOwn(STATUS_LABELS, status) ? STATUS_LABELS[status] : status;
}

export { STATUS_LABELS, CANCELLED_LABELS };

/**
 * E24.4 FR4 (OMEGA-1016) — customer copy for `job_outcomes.completion_status`.
 *
 * A SECOND value set, not a subset of `jobs.status`, which is why `statusLabel` could not be
 * stretched over it and why the Result row rendered a raw token for as long as it did.
 *
 * They overlap on FOUR spellings and `dispatched` is the dangerous one: in `jobs.status` it is a
 * lifecycle stage (the job is with the provider), and here it is the ABSENCE of a verdict on an
 * outcome row. One merged map would resolve it to one label and be wrong on one of the two
 * surfaces. The first draft of this comment said the overlap was three and the divergence two;
 * both numbers were wrong, and the test pinning the two sets against their own constraints said so.
 *
 * **The domain is ENFORCED, which is what makes this map checkable rather than a guess.**
 * `job_outcomes_completion_status_chk` pins it to these five plus NULL, and
 * `tests/test_lifecycle_status_sets.py` parses that constraint out of `db/schema.sql` and asserts
 * this map equals it. Adding a sixth label here without the constraint, or a sixth value there
 * without the label, turns that test red.
 *
 * `dispatched` and `approved_not_dispatched` are NOT final: `docs/database/decisions.md` records
 * them as the in-flight pair that "has no verdict yet", and the labels say so rather than implying
 * an outcome the row does not carry.
 */
const COMPLETION_STATUS_LABELS = {
  completed: "Completed",
  failed: "Failed",
  // NEUTRAL, for the reason `STATUS_LABELS.cancelled` is neutral (FR4b): this field does not
  // carry `failure_class` either, so nothing here substantiates WHO stopped the job.
  cancelled: "Stopped",
  // **These two must not reuse copy that already appears on the job detail screen**, and the
  // first draft did: `dispatched` was "In progress", which `StageTimeline` already renders as the
  // evidence line for a stage the job is still in. Two unrelated things saying the same words on
  // one screen is the ambiguity `JobDetailView`'s testid test was written against, rebuilt one
  // layer up. Caught because that test asserts the Result row resolves to exactly one node.
  dispatched: "No result yet",
  approved_not_dispatched: "Approved, not sent to a provider",
};

/**
 * The label for a `completion_status`, or `null` when there is nothing to show.
 *
 * Returns `null` rather than a placeholder so `OutcomeRow` hides the row, which is what a NULL
 * already did: the Result row has never rendered for an outcome with no completion status, and
 * FR3 bars a NULL from rendering as a blank or a `0`.
 *
 * `Object.hasOwn` rather than a bare index, matching `statusLabel`. The constraint makes a
 * prototype-chain key unreachable from the database today, which is a reason the guard is cheap
 * rather than a reason to leave it out: this function is exported and nothing stops a caller
 * passing something else.
 */
export function completionLabel(completionStatus) {
  if (!completionStatus) return null;
  return Object.hasOwn(COMPLETION_STATUS_LABELS, completionStatus)
    ? COMPLETION_STATUS_LABELS[completionStatus]
    : null;
}

export { COMPLETION_STATUS_LABELS };

/**
 * E24.4 FR2 — the stages a customer is shown, in the order a job passes through them.
 *
 * A GROUPING, not a per-status rename: `dispatching` and `dispatched` are one stage because the
 * difference between them is ours, not the customer's.
 *
 * `at` is the field that anchors the stage, and TWO stages deliberately have none.
 * "Waiting for your files" has no column of its own. "Starting" does have one,
 * `dispatch_claimed_at`, and it is deliberately NOT exposed on the wire, so rather than anchor the
 * stage to the wrong instant it gets no time at all. An absent anchor is honest; a plausible wrong
 * one is not, and only one of the two is visibly wrong later.
 */
export const STAGES = [
  { key: "submitted", label: "Submitted", at: "submitted_at", statuses: ["pending"] },
  { key: "inputs", label: "Waiting for your files", at: null, statuses: ["waiting_for_inputs"] },
  { key: "queued", label: "Waiting for capacity", at: "queued_at", statuses: ["queued"] },
  { key: "starting", label: "Starting", at: null, statuses: ["dispatching", "dispatched"] },
  { key: "running", label: "Running", at: "started_at", statuses: ["running"] },
  {
    key: "finished",
    label: "Finished",
    at: "completed_at",
    // E23 S13 — `credit_exhausted` is a FINISHED stage, and it is deliberately NOT in
    // NEVER_ACCEPTED_STATUSES below. A credit stop happens to a workload that was admitted,
    // queued, dispatched and running, so every stage before this one genuinely did happen and
    // marking them `skipped` would erase the work the customer is being charged for.
    statuses: ["completed", "failed", "cancelled", "vetoed", "rejected", "credit_exhausted"],
  },
];

/**
 * A status that ends the job WITHOUT it ever having been accepted for execution. Both are
 * refusals at the routing gate, so the stages between Submitted and Finished are not merely
 * unrecorded for these jobs -- they provably never happened.
 */
export const NEVER_ACCEPTED_STATUSES = new Set(["vetoed", "rejected"]);

/**
 * The stages that exist only AFTER the routing gate, and therefore the only ones a refusal can
 * speak for.
 *
 * The first version of the refusal rule applied to the whole of `STAGES`, which over-reached in
 * two directions at once and was caught in review, not by a test:
 *
 * - `submitted` was swept up, so a `vetoed` job whose `submitted_at` happened to be NULL
 *   (the column is NULLABLE -- `DEFAULT now()`, no NOT NULL) rendered a ONE-ROW timeline that
 *   denied the job had been submitted at all.
 * - `inputs` was swept up, and that stage sits BEFORE the gate. A customer who uploaded files
 *   through the managed-upload path and was then vetoed at routing had the one stage they
 *   personally spent time in erased -- while `input_ready_at`, still on the row, proved it
 *   happened. That shape exists in the database today: the upload-confirm path stamps
 *   `input_ready_at`, and the finalize CAS moves `waiting_for_inputs -> vetoed` without
 *   clearing it.
 *
 * "A refusal at the routing gate means the job never queued" is true of these three stages and
 * of nothing else.
 */
const POST_ROUTING_STAGE_KEYS = new Set(["queued", "starting", "running"]);

/**
 * Did this job reach the routing gate at all?
 *
 * `NEVER_ACCEPTED_STATUSES` alone was NOT the right test, and review caught it. Two production
 * writers terminalise a job that was still `waiting_for_inputs` and therefore never routed, under
 * statuses that are not in that set:
 *
 *   managed_upload.py  UPDATE jobs SET status = 'cancelled', input_failure_code = 'input_upload_aborted'
 *   managed_upload.py  UPDATE jobs SET status = 'failed',    input_failure_code = 'input_upload_expired'
 *
 * For those rows the refusal branch did not fire, so Waiting-for-capacity / Starting / Running
 * each rendered "No time recorded" -- which says we mislaid a time for a stage the job provably
 * never entered. That is this unit's original defect through a third door, and FR3 as rewritten
 * forbids exactly it.
 *
 * `input_failure_code` is the discriminator because it is written ONLY on the pre-routing input
 * path and is on the `JobDetailResponse` allowlist. The status cannot carry this on its own:
 * `cancelled` and `failed` are reached both before and after routing.
 */
function neverRouted(job) {
  return NEVER_ACCEPTED_STATUSES.has(job?.status) || Boolean(job?.input_failure_code);
}

/**
 * Did this job ever go through the managed-upload path?
 *
 * "Waiting for your files" is the one OPTIONAL stage: a job that supplied its inputs by reference
 * never entered it, and never will. Drawing it for those jobs -- which is the majority -- put
 * "Waiting for your files: No time recorded" on a page where the honest answer is that the stage
 * did not apply, and a customer reads that as "this happened and we lost the time".
 *
 * Found by LOOKING at the rendered page, not by a test. Every unit test passed on it, because the
 * output was the one the tests asked for; the defect was in what that output MEANS to a reader.
 *
 * The three signals are the ones E02.7 actually writes: the job is sitting in the stage now, its
 * inputs became ready (which only a managed upload records), or its upload failed.
 */
function usedManagedUpload(job) {
  return Boolean(
    job?.status === "waiting_for_inputs" ||
      job?.input_ready_at ||
      job?.input_failure_code ||
      job?.input_failure_reason,
  );
}

/**
 * Each stage's state for a given job: `current`, `done`, `no-time`, `not-reached` or `skipped`.
 *
 * THE DISTINCTION BETWEEN THE LAST TWO IS THE WHOLE DESIGN, and the first version of this
 * function did not have it: it inferred `done` from POSITION, so a `rejected` job -- which sits
 * at the last stage -- was shown as having queued and run. It never did. Its own test caught it.
 *
 * What we actually know, stage by stage, is less than position suggests:
 *
 * - A timestamp is proof. `done`, whatever route the job took to get there.
 * - Two stages have no column at all and never will (see `STAGES`), so their absence is proof of
 *   nothing either way.
 * - A NULL on a stage the job has demonstrably passed is COMMON, not exceptional: 29% of
 *   completed jobs on Dev have no `started_at`. Reading those as "never ran" would be false.
 *
 * So an earlier stage with no timestamp is `no-time` -- "we hold no time for this" -- which
 * claims neither that it happened nor that it did not. The one case where the stronger claim IS
 * warranted is a refusal at the routing gate: a `vetoed` or `rejected` job never entered the
 * queue, that is what those statuses MEAN, and it is the case where the weaker reading would most
 * mislead. That exception is named, not inferred, and it gets its own state: `skipped` stages are
 * NOT DRAWN (AC5). A row reading "Waiting for capacity -- not reached" on a job that was refused
 * before any of that applied is three lines of noise around one fact, and it invites the reader
 * to wonder what went wrong at a stage that never existed for them.
 */
export function stageStates(job) {
  const status = job?.status;
  const currentIndex = STAGES.findIndex((s) => s.statuses.includes(status));
  const neverAccepted = neverRouted(job);
  const managedUpload = usedManagedUpload(job);
  return STAGES.map((stage, i) => {
    const at = stage.at ? (job?.[stage.at] ?? null) : null;
    let state;
    if (i === currentIndex) state = "current";
    else if (stage.key === "inputs" && !managedUpload) state = "skipped";
    // The refusal rule sits ABOVE the timestamp branch, and the order is the fix rather than an
    // accident of writing. Below it, a `vetoed` job carrying a stray `queued_at` was redrawn as
    // "Waiting for capacity -- <time>", which is the ORIGINAL defect of this unit arriving
    // through a different door: a refused job shown to its customer as having queued and run.
    // No writer produces that row today, so it was latent -- but the AC6 scan below renders
    // exactly it and passed, which is the shape this whole unit exists to catch. The status is
    // the authority here: it is CHECK-constrained and written by the gate itself, whereas a
    // timestamp on a stage the gate refused is drift.
    else if (neverAccepted && POST_ROUTING_STAGE_KEYS.has(stage.key)) state = "skipped";
    else if (at) state = "done";
    else if (currentIndex === -1 || i > currentIndex) state = "not-reached";
    else state = "no-time";
    return { ...stage, at, state };
  });
}

export function StatusBadge({ status, failureClass }) {
  return (
    <span
      // E24.4 — `uppercase` is gone. It was harmless over one-word enum tokens and is not over
      // sentence-case copy: it would render "STOPPED: TIME LIMIT REACHED" at a customer.
      className={`px-1.5 py-0.5 rounded-none text-xs font-medium tracking-wide ${
        STATUS_STYLES[status] || "bg-surface2 text-fg2"
      }`}
    >
      {statusLabel(status, failureClass)}
    </span>
  );
}

/**
 * One label/value row. Hides itself entirely on a null/empty value, which is the
 * convention every caller relies on for its empty states.
 *
 * `mono` selects the BREAK MODE as well as the font, and the pairing is not
 * incidental (OMEGA-898). Every `mono` value is an unbreakable token where an
 * arbitrary break is wanted and correct: a job id, `busybox:latest`, a provider
 * run id, a region slug. `break-all` splits those at the column edge instead of
 * overflowing. Every `mono={false}` value is human prose, and there `break-all`
 * is actively wrong -- it splits mid-word, so the reason on a vetoed job rendered
 * as "the providers that serve this workloa / d's region" in the narrow identity
 * column. `break-words` breaks a word only when it cannot fit, so prose wraps at
 * spaces and a freak long token still cannot overflow.
 *
 * Nothing that was correct changes: for the short `mono={false}` values (workload
 * name, project, formatted times) the two modes render identically, and the only
 * other long one, `outcome.failure_reason`, is prose that improves the same way.
 */
export function OutcomeRow({ label, value, mono = true }) {
  if (value == null || value === "") return null;
  return (
    <div className="flex justify-between gap-3 text-sm py-0.5">
      <span className="text-fg2">{label}</span>
      <span className={`${mono ? "font-mono break-all" : "break-words"} text-fg text-right`}>
        {String(value)}
      </span>
    </div>
  );
}

/**
 * A duration in seconds as human copy. Returns null for anything that is not a usable number,
 * so a caller's `{fmtDuration(x) && ...}` guard covers "absent" and "unparseable" alike.
 *
 * The backend types `duration_secs` as `numeric(10,2)`, which asyncpg hands to Pydantic as a
 * Decimal and Pydantic serialises as a JSON number -- but a `numeric` column is exactly the shape
 * that arrives as a STRING through some stacks, so parse rather than assume.
 */
export function fmtDuration(secs) {
  // `Number("")` and `Number("  ")` are 0, not NaN, so a blank would render as "0s" -- a
  // measured zero where there is no measurement at all. Reject the blank before coercing.
  const n = typeof secs === "string" ? (secs.trim() === "" ? NaN : Number(secs)) : secs;
  if (typeof n !== "number" || !Number.isFinite(n) || n < 0) return null;
  const whole = Math.round(n);
  const h = Math.floor(whole / 3600);
  const m = Math.floor((whole % 3600) / 60);
  const s = whole % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

/**
 * The copy for an elapsed figure that CANNOT be computed because its anchor is NULL.
 *
 * Never `0s`. A job dispatched twenty minutes ago that has not been picked up has been running
 * for no time at all, and "0s" is the one reading that makes that indistinguishable from a job
 * that started this instant. `started_at` is NULL on 29% of even COMPLETED jobs (E24.4 D5), so
 * this is a common path, not a defensive branch.
 */
export const NOT_STARTED_VALUE = "Not started yet";

/** Seconds between an ISO instant and `now`, or null if the instant is missing or unparseable. */
function secsSince(iso, now) {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  return Math.max(0, (now - t) / 1000);
}

/**
 * E24.4 — the elapsed figures for a job, per the PRD's rendering contract.
 *
 * Pure, and takes `now` as an argument rather than reading the clock: the component owns the
 * ticking so this stays assertable at a fixed instant.
 *
 * THE ONE RULE THE WHOLE TABLE IS BUILT ON: every figure names the instant it counts from. That
 * is why each carries a `note`, why the Starting stage gets no figure of its own (its real
 * anchor, `dispatch_claimed_at`, is deliberately not on the wire), and why a NULL anchor produces
 * `NOT_STARTED_VALUE` rather than a number.
 */
export function elapsedFigures(job, now) {
  const status = job?.status;
  const terminal = TERMINAL_STATUSES.includes(status);
  const figs = [];

  // A refusal at the routing gate gets NO figures, per the PRD's contract row. Not a defensive
  // branch: a `vetoed` job carrying a `duration_secs` rendered "Run time ... measured on the
  // provider" for a job that never reached a provider, which is a false statement about where
  // the number came from rather than merely a surplus row.
  if (NEVER_ACCEPTED_STATUSES.has(status)) return figs;

  if (status === "queued") {
    const secs = secsSince(job?.queued_at, now);
    figs.push({
      key: "queued",
      label: "Waiting for capacity for",
      value: secs === null ? NOT_STARTED_VALUE : fmtDuration(secs),
      note: job?.queued_at ? `Since ${fmtTime(job.queued_at)}.` : "We hold no time for when it entered the queue.",
    });
  }

  // A job at the Starting stage gets NO per-stage TIMER (the PRD's contract row), and it must not
  // be given the next stage's LABEL either. "Running for" printed directly beneath a stage row
  // reading "Starting (current)" is two claims on one panel that contradict each other, and it
  // shipped that way until review. So Starting reports whether running has begun -- which is the
  // question the customer is actually asking there -- and only `running` gets an elapsed figure.
  // `dispatching` and `dispatched` are ONE customer stage but two different facts, and the note
  // must not blur them. `dispatching` is the worker's CLAIM -- taken before the provider
  // submission, which is why a lease-reclaim path exists for jobs stuck in it -- so the job is
  // NOT on a provider yet. Only `dispatched` has a `provider_run_id`. The earlier single note
  // said "Your job is on a provider" for both, which was false for every `dispatching` job.
  if (status === "dispatching" || status === "dispatched") {
    figs.push({
      key: "running",
      label: "Started running",
      value: job?.started_at ? fmtTime(job.started_at) : NOT_STARTED_VALUE,
      note: job?.started_at
        ? null
        : status === "dispatching"
          ? "We are sending your job to a provider."
          : "Your job is with a provider and waiting for the machine it asked for.",
    });
  }

  // A `running` job with no `started_at` is 29% of the population, and the honest reading is that
  // the start time is UNRECORDED -- not that the job has not started. "Started running: Not
  // started yet" under a row saying "Running (current)" was the first contradiction swapped for a
  // second one; the fix is to say which of the two facts we actually hold.
  if (status === "running") {
    const secs = secsSince(job?.started_at, now);
    if (secs === null) {
      figs.push({
        key: "running",
        label: "Running for",
        value: "No start time recorded",
        note: "Your job is running. We do not hold the instant it started, so there is no elapsed figure.",
      });
    } else {
      figs.push({
        key: "running",
        label: "Running for",
        value: fmtDuration(secs),
        note: `Since ${fmtTime(job.started_at)}.`,
      });
    }
  }

  // The overall figure, which is the only one a non-terminal job is guaranteed to have: every job
  // has a `submitted_at`. It is deliberately NOT shown on a terminal job, where the run time below
  // is the figure that answers the question and two totals side by side would invite subtraction.
  if (!terminal) {
    const secs = secsSince(job?.submitted_at, now);
    if (secs !== null) {
      figs.push({
        key: "overall",
        label: "Since you submitted",
        value: fmtDuration(secs),
        note: `Submitted ${fmtTime(job.submitted_at)}.`,
      });
    }
  }

  if (terminal) {
    const runTime = fmtDuration(job?.duration_secs);
    if (runTime) {
      figs.push({
        key: "total",
        label: "Run time",
        value: runTime,
        // NOT `completed_at` minus `started_at` on our clock — our `started_at` is when we
        // OBSERVED the job running, and this is the provider's own measurement of the workload
        // (stop minus start on the task). Naming whose clock it is keeps the two from being read
        // as the same number.
        note: "Measured on the provider, from when your workload started running to when it stopped.",
      });
    }
  }

  return figs;
}

/**
 * E24.4 FR2/FR3/FR5 — the job's progress through the stages in `STAGES`, plus its elapsed figures.
 *
 * WHAT IT WILL NOT DO, because these are the failure modes the unit exists to prevent:
 *
 * - It never infers a time. A stage with no timestamp says so; it does not borrow the neighbouring
 *   stage's instant, and it does not present `submitted_at` as a proxy for when anything else
 *   happened. Roughly 29% of completed jobs have a NULL `started_at` and this renders that as an
 *   absence, which is the truth, rather than as a plausible number nobody can check.
 * - It never says a stage was skipped when it was not reached, nor draws one that never applied.
 * - Terminal with no `completed_at` or `duration_secs` is a NORMAL case, not an error. It is 98.7%
 *   of `cancelled` jobs. The outcome renders; the total is simply absent.
 *
 * FR5 (accessible): the stage list is an ordered list so a screen reader announces position and
 * count, the current stage carries `aria-current="step"` AND says "(current)" in visible text, and
 * every state word is rendered as text. Nothing here is behind a hover or a colour: there is no
 * tooltip, no title attribute and no interactive control, so there is nothing a keyboard user can
 * fail to reach.
 *
 * The clock ticks ONLY while the job is non-terminal. A settled job's figures cannot change, so
 * re-rendering it every second would be waste — and an advancing counter on a finished job would
 * be an outright lie.
 */
export function StageTimeline({ job }) {
  const terminal = TERMINAL_STATUSES.includes(job?.status);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (terminal) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [terminal]);

  const stages = stageStates(job).filter((s) => s.state !== "skipped");
  const figures = elapsedFigures(job, now);
  return (
    <div data-testid="job-stage-timeline">
      <ol className="space-y-0" aria-label="Job progress">
        {stages.map(({ key, label, at, state }) => {
          // The last stage is named by the OUTCOME the job actually reached ("Completed",
          // "Not accepted", ...) rather than by the generic "Finished", so the row a customer
          // reads last is the answer they came for. Before the job is terminal it keeps the
          // generic name, because no outcome exists to name yet.
          const shown =
            key === "finished" && state === "current"
              ? statusLabel(job?.status, job?.outcome?.failure_class)
              : label;
          let evidence;
          if (at) evidence = fmtTime(at);
          // "In progress" is only true of a stage the job is STILL IN. A terminal job's last
          // stage is current AND finished, so a missing `completed_at` there is a missing record,
          // not activity -- a vetoed job read "Not run (current): In progress", which says the
          // refusal is ongoing. Every vetoed and rejected job on Dev has a NULL `completed_at`,
          // so this was the only thing that row could ever have said.
          else if (state === "current") evidence = terminal ? "No time recorded" : "In progress";
          else if (state === "no-time") evidence = "No time recorded";
          else evidence = "Not reached";
          return (
            <li
              key={key}
              data-testid={`stage-${key}`}
              data-state={state}
              aria-current={state === "current" ? "step" : undefined}
              className="flex justify-between gap-3 text-sm py-0.5"
            >
              <span className={state === "not-reached" ? "text-fg3" : "text-fg2"}>
                {shown}
                {state === "current" && <span className="text-fg3"> (current)</span>}
              </span>
              <span className={`break-words text-right ${state === "not-reached" ? "text-fg3" : "text-fg"}`}>
                {evidence}
              </span>
            </li>
          );
        })}
      </ol>
      {figures.length > 0 && (
        <div className="mt-2 pt-2 border-t border-line/60" data-testid="job-elapsed">
          {figures.map(({ key, label, value, note }) => (
            <div key={key} data-testid={`elapsed-${key}`}>
              <OutcomeRow label={label} value={value} mono={false} />
              {note && <div className="text-xs text-fg3">{note}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
